import React, { useEffect, useState, useRef, KeyboardEvent } from 'react';
import './journal.css';
import {
  Paperclip,
  Users,
  MapPin,
  Tag as TagIcon,
  Clock,
  ListTree,
  Goal,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useSupabase } from '../../../contexts/SupabaseContext';
import { useAuth } from '../../../contexts/AuthContext';
import type { Task } from '../../../types/database';

const formatDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

// Helper: identify headings that should render inside a bordered box
const BOX_HEADING_REGEX = /^(Things I am Grateful for :|Goals :)/i;

const Journal = () => {
  const {
    tasks,
    loadTasks,
    createTask,
    updateTask,
    deleteTask,
  } = useSupabase();
  const { user } = useAuth();

  const [entries, setEntries] = useState<Task[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [unsaved, setUnsaved] = useState(false);

  // Inline editing state for existing entries
  const [editingEntry, setEditingEntry] = useState<Task | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editUnsaved, setEditUnsaved] = useState(false);
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const editFormRef = useRef<HTMLDivElement | null>(null); // Ref for the editing container
  const newTileRef = useRef<HTMLDivElement | null>(null); // Ref for the static "new entry" tile

  // Settings
  const [timestampEnabled, setTimestampEnabled] = useState(true);

  const formRef = useRef<HTMLFormElement | null>(null);

  // At top within function component, after state declarations
  const collapsedNewEntry = Boolean(editingEntry);
  const activeEntryIndex = editingEntry ? entries.findIndex(e => e.id === editingEntry.id) : -1;
  const nextEntry =
    editingEntry && activeEntryIndex >= 0 && activeEntryIndex < entries.length - 1
      ? entries[activeEntryIndex + 1]
      : null;

  // In new-entry textarea component add ref
  const newTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const insertTimestamp = () => `${formatDate(new Date())} • ${formatTime(new Date())}\n`;

  // Helper to insert arbitrary text at the current cursor position of a textarea
  const insertTextAtCursor = (
    ref: React.RefObject<HTMLTextAreaElement>,
    value: string,
    setter: (val: string) => void,
    text: string,
  ) => {
    const el = ref.current;
    if (!el) {
      // If ref is unavailable, append to end
      setter(value + text);
      return;
    }
    const pos = el.selectionStart;
    const newVal = value.slice(0, pos) + text + value.slice(pos);
    setter(newVal);
    // move cursor just after inserted text
    requestAnimationFrame(() => {
      const newPos = pos + text.length;
      el.setSelectionRange(newPos, newPos);
      el.focus();
    });
  };

  const handleTextareaKey = (
    e: KeyboardEvent<HTMLTextAreaElement>,
    ref: React.RefObject<HTMLTextAreaElement>,
    value: string,
    setter: (val: string) => void,
    markUnsaved: () => void,
  ) => {
    const el = ref.current;
    if (!el) return;

    // 1. Timestamp insertion on "/d"
    if (e.key === 'd') {
      const pos = el.selectionStart;
      if (pos >= 1 && value[pos - 1] === '/') {
        e.preventDefault();
        const ts = insertTimestamp();
        const newVal = value.slice(0, pos - 1) + ts + value.slice(pos);
        setter(newVal);
        markUnsaved();
        requestAnimationFrame(() => {
          const newPos = pos - 1 + ts.length;
          el.setSelectionRange(newPos, newPos);
        });
        return;
      }
    }

    // 2. Skip blank line between numbered templates
    if (e.key === 'Enter') {
      const pos = el.selectionStart;
      // Identify start & end of current line
      const prevNL = value.lastIndexOf('\n', pos - 1);
      const lineStart = prevNL === -1 ? 0 : prevNL + 1;
      const nextNL = value.indexOf('\n', pos);
      const lineEnd = nextNL === -1 ? value.length : nextNL;
      const currentLine = value.slice(lineStart, lineEnd);

      // Does the current line begin with a numbered bullet?
      const bulletPrefixMatch = currentLine.match(/^\s*\d+\.\s*/);
      if (bulletPrefixMatch) {
        const restOfLine = currentLine.slice(bulletPrefixMatch[0].length);
        // If there's no content yet after the bullet, keep the caret here to allow typing
        if (restOfLine.trim().length === 0) {
          return; // allow default behaviour (newline) so user can type normally
        }

        e.preventDefault();

        // Move caret to bullet on the following line (if exists)
        const nextLineStart = lineEnd + 1;
        if (nextLineStart >= value.length) return; // no next line

        const nextLine = value.slice(nextLineStart);
        const nextBulletMatch = nextLine.match(/^\s*\d+\.\s*/);
        if (nextBulletMatch) {
          const newPos = nextLineStart + nextBulletMatch[0].length;
          requestAnimationFrame(() => {
            el.setSelectionRange(newPos, newPos);
          });
        }
        return;
      }
    }
  };

  // Load journal tasks
  useEffect(() => {
    loadTasks('journal');
  }, [loadTasks]);

  // Update local state when tasks change
  useEffect(() => {
    const journalTasks = tasks
      .filter(t => t.category === 'journal')
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)); // descending
    setEntries(journalTasks);
  }, [tasks]);

  // Save brand‑new entry from the top form
  const saveNewEntry = async () => {
    try {
      if (!unsaved || !content.trim() || !user) return;

      const now = new Date();

      await createTask({
        user_id: user.id,
        title: title || `Journal – ${formatDate(now)}`,
        description: content.trim(),
        category: 'journal',
        status: 'todo',
      });

      // Clear inputs
      setTitle('');
      setContent('');
      setUnsaved(false);
    } catch (error) {
      console.error('Failed to save journal entry:', error);
    }
  };

  // Save edits to an existing entry (inline)
  const saveEditedEntry = async () => {
    try {
      if (!editingEntry || !editUnsaved || !editContent.trim()) return;

      await updateTask(editingEntry.id, {
        title: editTitle.trim() || `Journal – ${formatDate(new Date(editingEntry.created_at))}`,
        description: editContent.trim(),
      });

      // Reset inline editing state
      setEditingEntry(null);
      setEditContent('');
      setEditUnsaved(false);
    } catch (error) {
      console.error('Failed to update journal entry:', error);
    }
  };

  // --- MODIFIED: Reverted to simple handleDeleteEntry with window.confirm ---
  const handleDeleteEntry = async (entryId: string) => {
    // Use window.confirm for simplicity since AlertDialog component is not available
    if (window.confirm("You are about to delete a Journal Entry. Press confirm to continue.")) {
      try {
        await deleteTask(entryId);
        // If currently editing the deleted entry, close the editor
        if (editingEntry?.id === entryId) {
          cancelEditing();
        }
      } catch (error) {
        console.error('Failed to delete journal entry:', error);
        // Optionally show user feedback here
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveNewEntry();
    setEditUnsaved(false);
  };

  // mark unsaved on change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setUnsaved(true);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    setUnsaved(true);
  };

  // --- Need cancelEditing defined before useEffect that uses it ---
  const cancelEditing = () => {
    setEditingEntry(null);
    setEditContent('');
    setEditUnsaved(false);
  };

  // Auto‑save when clicking outside new‑entry form OR inline editor
  useEffect(() => {
    const handleClick = async (e: MouseEvent) => {
      const targetNode = e.target as Node;

      // Save NEW entry if click outside formRef
      if (formRef.current && !formRef.current.contains(targetNode)) {
        // Check if content exists before saving to avoid empty saves on blur
        if (content.trim()) {
           await saveNewEntry();
        }
      }

      // When editing, only close the editor (thereby opening new-entry pane) if the user clicks the dedicated "new entry" tile.
      if (
        editingEntry &&
        editFormRef.current &&
        !editFormRef.current.contains(targetNode)
      ) {
        // Click landed outside the editor. Check if it's inside the new-entry tile.
        if (newTileRef.current && newTileRef.current.contains(targetNode as Node)) {
          // If there are unsaved changes, save first; then close editor.
          if (editUnsaved && editContent.trim()) {
            await saveEditedEntry();
          }
          cancelEditing();
        }
        // Otherwise, ignore the click so the editor stays open.
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
    // Depend on states that trigger saves or state changes on click outside
  }, [content, editContent, unsaved, editUnsaved, editingEntry, saveNewEntry, saveEditedEntry, cancelEditing]);

  // User selects an existing entry to edit inline
  const handleEditEntry = (entry: Task) => {
    setEditingEntry(entry);
    setEditContent(entry.description || '');
    setEditTitle(entry.title || '');
    setTimeout(() => {
      const len = entry.description?.length ?? 0;
      editTextareaRef.current?.focus();
      editTextareaRef.current?.setSelectionRange(len, len);
    }, 0);
    setEditUnsaved(false);
  };

  // Insert after state declarations but before return, define helper to render entry
  const renderEntry = (entry: Task) => {
    const isEditing = editingEntry?.id === entry.id;
    // helper to transform plain text into JSX with boxes
    const renderDescription = (desc: string) => {
      const lines = desc.split('\n');
      const elements = [] as React.ReactNode[];
      for (let i = 0; i < lines.length; ) {
        const line = lines[i];
        if (BOX_HEADING_REGEX.test(line.trim())) {
          const boxLines: string[] = [line];
          let j = i + 1;
          while (j < lines.length && lines[j].trim() !== '') {
            boxLines.push(lines[j]);
            j++;
          }
          elements.push(
            <div
              key={`box-${i}`}
              className="border border-zinc-300 dark:border-zinc-600 rounded-md p-3 my-3"
            >
              {boxLines.map((bl, idx) => (
                <p
                  key={idx}
                  className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-zinc-700 dark:text-zinc-200"
                >
                  {bl}
                </p>
              ))}
            </div>
          );
          i = j; // move past processed lines
        } else {
          elements.push(
            <p
              key={`p-${i}`}
              className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-zinc-700 dark:text-zinc-200"
            >
              {line}
            </p>
          );
          i++;
        }
      }
      return elements;
    };
    return (
      <article
        key={entry.id}
        onClick={() => {
          handleEditEntry(entry);
        }}
        className={`bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-4 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors ${
          isEditing
            ? 'border-2 border-primary journal-entry-expanded'
            : 'journal-entry-collapsed'
        }`}
        ref={isEditing ? editFormRef : null}
      >
        <header className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isEditing ? (
              <input
                type="text"
                value={editTitle}
                onChange={e => {
                  setEditTitle(e.target.value);
                  setEditUnsaved(true);
                }}
                className="w-full bg-transparent text-base font-semibold journal-title focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 -mx-1"
                placeholder="Title"
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <h3 className="text-base font-semibold journal-title truncate pl-1">
                {entry.title || 'Untitled'}
              </h3>
            )}
          </div>
          <time
            className="text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0 ml-2"
            dateTime={entry.created_at}
          >
            {formatDate(new Date(entry.created_at))} • {formatTime(new Date(entry.created_at))}
          </time>
        </header>
        {isEditing ? (
          <div className="mt-2 flex flex-col gap-2 flex-grow">
            <textarea
              ref={editTextareaRef}
              value={editContent}
              onChange={e => {
                setEditContent(e.target.value);
                setEditUnsaved(true);
              }}
              onKeyDown={e => handleTextareaKey(e, editTextareaRef, editContent, setEditContent, () => setEditUnsaved(true))}
              className="w-full flex-grow min-h-0 resize-none overflow-y-auto bg-transparent rounded p-2 text-sm leading-relaxed focus:outline-none journal-edit-textarea"
              onClick={e => e.stopPropagation()}
            />
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (editUnsaved && editContent.trim()) {
                    await saveEditedEntry();
                  } else {
                    cancelEditing();
                  }
                }}
                className="px-3 py-1 rounded text-sm bg-zinc-100 dark:bg-zinc-700 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-600"
              >
                Close
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteEntry(entry.id);
                  }}
                  className="px-3 py-1 rounded text-sm bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/60"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2">
            {entry.description ? (
              <div className="space-y-1">{renderDescription(entry.description)}</div>
            ) : (
              <p className="text-sm text-zinc-400 italic">No content.</p>
            )}
          </div>
        )}
      </article>
    );
  };

  // Add helper for tile
  const renderTile = (entry: Task) => {
    const isSelected = editingEntry?.id === entry.id;

    // Remove timestamp-only lines from preview
    const cleanedDescription = (entry.description || '')
      .split('\n')
      .filter(line => {
        const trimmed = line.trim();
        // Exclude lines that contain the bullet separator (our timestamp format) or just a time like "12:41 PM"
        if (trimmed.includes(' • ')) return false;
        if (/\d{1,2}:\d{2}\s?(AM|PM)/i.test(trimmed)) return false;
        return trimmed.length > 0;
      })
      .join(' ');

    return (
      <article
        key={entry.id}
        onClick={() => handleEditEntry(entry)}
        className={`bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors ${
          isSelected ? 'border-primary' : ''
        }`}
      >
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-semibold text-sm truncate journal-title pl-1">
            {entry.title || 'Untitled'}
          </h4>
          <span className="text-[10px] text-zinc-400 flex-shrink-0 ml-2">
            {formatDate(new Date(entry.created_at))}
          </span>
        </div>
        {cleanedDescription && (
          <p className="text-xs text-zinc-600 dark:text-zinc-400 line-clamp-3 whitespace-pre-wrap">
            {cleanedDescription}
          </p>
        )}
      </article>
    );
  };

  return (
    <section className="flex h-full gap-6 py-6">
      {/* LEFT COLUMN */}
      <div className="flex flex-col flex-1 gap-4 overflow-hidden">
        {editingEntry ? (
          renderEntry(editingEntry)
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`bg-white dark:bg-zinc-800 shadow-sm border border-zinc-200 dark:border-zinc-700 rounded-lg ${
              collapsedNewEntry ? 'new-entry-collapsed border-dashed' : 'p-6 flex flex-col gap-4 rounded-lg flex-grow'
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {collapsedNewEntry ? (
                  <span className="text-base font-semibold text-zinc-400 pl-2" style={{fontFamily:'Playfair Display, serif'}}>New Entry</span>
                ) : (
                  <input
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={handleTitleChange}
                    className="w-full bg-transparent text-xl font-semibold focus:outline-none placeholder:opacity-60 journal-title"
                  />
                )}
              </div>
              {!collapsedNewEntry && (
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  {formatDate(new Date())} • {formatTime(new Date())}
                </div>
              )}
            </div>

            {/* Content (hidden when collapsed) */}
            {!collapsedNewEntry && (
              <textarea
                ref={newTextareaRef}
                placeholder="Write your journal entry..."
                value={content}
                onChange={handleContentChange}
                onKeyDown={(e) => handleTextareaKey(e, newTextareaRef, content, setContent, () => setUnsaved(true))}
                className="w-full flex-1 min-h-0 resize-none overflow-y-auto bg-transparent border-none focus:outline-none text-sm leading-relaxed"
              />
            )}

            {/* Action bar (hidden when collapsed) */}
            {!collapsedNewEntry && (
            <div className="flex items-center justify-between pt-2 border-t border-dashed border-zinc-200 dark:border-zinc-700">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Attach photo (coming soon)"
                >
                  <Paperclip size={18} />
                </button>
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Add people (coming soon)"
                >
                  <Users size={18} />
                </button>
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Add location (coming soon)"
                >
                  <MapPin size={18} />
                </button>
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Add tags (coming soon)"
                >
                  <TagIcon size={18} />
                </button>
                {/* Timestamp toggle */}
                <button
                  type="button"
                  onClick={() => setTimestampEnabled(prev => !prev)}
                  className={`p-1 ${timestampEnabled ? 'text-primary-500' : 'text-zinc-500'} hover:text-primary-500`}
                  title={`Timestamp ${timestampEnabled ? 'on' : 'off'}`}
                >
                  <Clock size={18} />
                </button>
                {/* Gratefulness template */}
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Gratefulness"
                  onClick={() => {
                    const el = newTextareaRef.current;
                    const insertion = '\nThings I am Grateful for :\n1.  \n2.  \n3.  \n';
                    const caretPos = el ? el.selectionStart : content.length;
                    const newVal = content.slice(0, caretPos) + insertion + content.slice(caretPos);
                    setContent(newVal);
                    setUnsaved(true);
                    requestAnimationFrame(() => {
                      const targetPos = caretPos + insertion.indexOf('1.') + '1. '.length;
                      if (el) {
                        el.setSelectionRange(targetPos, targetPos);
                        el.focus();
                      }
                    });
                  }}
                >
                  <ListTree size={18} />
                </button>

                {/* Goals template */}
                <button
                  type="button"
                  className="p-1 text-zinc-500 hover:text-primary-500"
                  title="Goals"
                  onClick={() => {
                    const el = newTextareaRef.current;
                    const insertion = '\nGoals :\n1.  \n2.  \n3.  \n';
                    const caretPos = el ? el.selectionStart : content.length;
                    const newVal = content.slice(0, caretPos) + insertion + content.slice(caretPos);
                    setContent(newVal);
                    setUnsaved(true);
                    requestAnimationFrame(() => {
                      const targetPos = caretPos + insertion.indexOf('1.') + '1. '.length;
                      if (el) {
                        el.setSelectionRange(targetPos, targetPos);
                        el.focus();
                      }
                    });
                  }}
                >
                  <Goal size={18} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {unsaved && (
                  <span className="text-xs text-zinc-500">Unsaved changes</span>
                )}
                <button
                  type="submit"
                  disabled={!unsaved || !content.trim()}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    unsaved && content.trim()
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-zinc-100 text-zinc-400 dark:bg-zinc-700 dark:text-zinc-500 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>
            </div>
            )}
          </form>
        )}
      </div>

      {/* RIGHT COLUMN */}
      <div className="flex flex-col w-80 flex-shrink-0 gap-4 overflow-hidden">
        {/* New Entry Tile */}
        <article
          key="new-tile"
          onClick={() => cancelEditing()}
          className="bg-white dark:bg-zinc-800 border border-dashed border-zinc-300 dark:border-zinc-600 rounded-lg p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
          ref={newTileRef}
        >
          <h4 className="font-semibold text-sm mb-1 text-zinc-400 pl-1">Title</h4>
          <p className="text-xs text-zinc-400">Write your journal entry...</p>
        </article>

        {/* Scrollable past entries */}
        <div className="flex flex-col flex-grow gap-4 overflow-y-auto">
          {entries.length === 0 && (
            <p className="text-sm text-zinc-500">No journal entries yet.</p>
          )}
          {entries.map(renderTile)}
        </div>
      </div>
    </section>
  );
};

export default Journal; 